import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getInventoryItemDetails,
} from '../services/api';

export const useInventoryOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [stats, setStats] = useState({
    total_items: 0,
    total_quantity: 0,
    low_stock_items: 0,
    added_by_breakdown: [],
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});

  const loadInventoryItems = useCallback(
    async (page, rowsPerPage, searchTerm, nameFilter, minQuantity, maxQuantity, addedByFilter) => {
      await withLoading(async () => {
        try {
          const response = await getInventoryItems(
            page + 1, // API expects 1-based pagination
            rowsPerPage,
            searchTerm,
            nameFilter,
            minQuantity,
            maxQuantity,
            addedByFilter
          );

          if (response.data) {
            setInventoryItems(response.data.results || []);
            setTotalCount(response.data.pagination_info?.count || 0);

            // Update stats if included in response
            if (response.data.stats) {
              setStats(response.data.stats);
            }

            // Update filter options if included in response
            if (response.data.filter_options) {
              setFilterOptions(response.data.filter_options);
            }
          }
        } catch (error) {
          console.error('Error loading inventory items:', error);
          toast.error('Failed to load inventory items');
        }
      });
    },
    [withLoading]
  );

  const createInventoryItemRecord = useCallback(async (itemData) => {
    try {
      await createInventoryItem(itemData);
      toast.success('Inventory item created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating inventory item:', error);
      toast.error('Failed to create inventory item');
      return false;
    }
  }, []);

  const updateInventoryItemRecord = useCallback(async (itemId, itemData) => {
    try {
      await updateInventoryItem(itemId, itemData);
      toast.success('Inventory item updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating inventory item:', error);
      toast.error('Failed to update inventory item');
      return false;
    }
  }, []);

  const deleteInventoryItemRecord = useCallback(async (itemId) => {
    try {
      await deleteInventoryItem(itemId);
      toast.success('Inventory item deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      toast.error('Failed to delete inventory item');
      return false;
    }
  }, []);

  const getInventoryItemDetailsRecord = useCallback(async (itemId) => {
    try {
      const response = await getInventoryItemDetails(itemId);
      return response.data;
    } catch (error) {
      console.error('Error getting inventory item details:', error);
      toast.error('Failed to get inventory item details');
      return null;
    }
  }, []);

  return {
    isLoading,
    inventoryItems,
    stats,
    totalCount,
    filterOptions,
    loadInventoryItems,
    createInventoryItemRecord,
    updateInventoryItemRecord,
    deleteInventoryItemRecord,
    getInventoryItemDetailsRecord,
  };
};